import { classSet } from '@platform/ui-library';
import { FC } from 'react';
import { FormControlWithOptionProps, Option } from '@app/types';
import { ControlWithFormField, FormFieldControlProps } from './form-field';
import styles from './CheckboxGroup.module.scss';

export type CheckboxGroupControlProps<TItem> = (
  FormControlWithOptionProps<
    TItem[],
    Option<TItem> & { checked: boolean; }
  >
  & {
    options: Option<TItem>[];
    /* How to display the group @default 'column' for 3+ @see options. */
    theme?: 'row' | 'column';
  }
);

/** Checkbox button group form control used to select a value out of an array of options. */
function CheckboxGroupControl<TItem>({
  onChange,
  options,
  theme = 'column',
  value,
  ...inputProps
}: CheckboxGroupControlProps<TItem>): ReturnType<FC> {
  theme ??= (options.length > 2) ? 'column' : 'row';
  const groupClassName = classSet([
    styles.checkboxOptions,
    (theme === 'row') ? 'flex-gap flex-row flex-wrap' : 'flex-column']
  );
  const isOptionChecked = (option: Option<TItem>): boolean => {
    return value?.includes(option.value) ?? false;
  };

  return (
    <div className={groupClassName}>
      {options.map((option) =>
        <label className={styles.checkboxOption} key={option.label}>
          <input
            {...inputProps}
            type="checkbox"
            checked={isOptionChecked(option)}
            onChange={(event) => {
              value ??= [];
              const { checked } = event.target;
              value = (checked) ? [...value, option.value] : (
                value.filter((valueItem) => valueItem !== option.value)
              );
              onChange(value, { ...option, checked });
            }}
          />
          {option.label}
        </label>
      )}
    </div>
  );
}

export type CheckboxGroupProps<T> = CheckboxGroupControlProps<T> & FormFieldControlProps;

export function CheckboxGroup<T>(props: CheckboxGroupProps<T>): ReturnType<FC> {
  return (
    <ControlWithFormField control={CheckboxGroupControl<T>} {...props} />
  );
}
